var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback } from 'react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
/**
 * Suppress global API-error-interceptor notifications so that callers
 * can show their own, more-contextual messages without duplicates.
 */
const SUPPRESS_GLOBAL_NOTIFICATIONS = {
    no400Handling: true,
    no500Handling: true,
};
const isWrappedSavedIdpResponse = (response) => {
    return response != null && typeof response === 'object' && !('key' in response) && 'data' in response;
};
const getSavedIdp = (response) => {
    var _a;
    if (isWrappedSavedIdpResponse(response)) {
        return (_a = response.data) !== null && _a !== void 0 ? _a : null;
    }
    return response !== null && response !== void 0 ? response : null;
};
export const useIdpApi = () => {
    const iamClient = useApiClient(ApiClientService.IAM);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const testConnection = useCallback((params) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield iamClient.post('/v1/idps/oidc', {
                validateIssuerOnly: true,
                configuration: {
                    clientId: params.clientId,
                    issuer: params.issuer,
                },
            }, SUPPRESS_GLOBAL_NOTIFICATIONS);
            addNotification({
                title: translate('Connection successful'),
                subtitle: translate('Successfully connected to the identity provider.'),
                type: 'success',
            });
            return true;
        }
        catch (_a) {
            addNotification({
                title: translate('Connection failed'),
                subtitle: translate('Failed to connect to the identity provider. Please check your settings.'),
                type: 'error',
            });
            return false;
        }
    }), [iamClient, addNotification, translate]);
    const saveIdp = useCallback((payload, existingIdp) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (existingIdp) {
                const { data: response } = yield iamClient.put(`/v1/idps/oidc/${existingIdp.key}`, payload, SUPPRESS_GLOBAL_NOTIFICATIONS);
                addNotification({
                    title: translate('Identity provider updated'),
                    subtitle: translate('The identity provider has been updated successfully.'),
                    type: 'success',
                });
                return getSavedIdp(response);
            }
            const { data: response } = yield iamClient.post('/v1/idps/oidc', payload, SUPPRESS_GLOBAL_NOTIFICATIONS);
            addNotification({
                title: translate('Identity provider created'),
                subtitle: translate('The identity provider has been created successfully.'),
                type: 'success',
            });
            return getSavedIdp(response);
        }
        catch (error) {
            console.error('Error saving identity provider', error);
            addNotification({
                title: translate('Error'),
                subtitle: translate('Failed to save identity provider. Please try again.'),
                type: 'error',
            });
            return null;
        }
    }), [iamClient, addNotification, translate]);
    return { testConnection, saveIdp };
};
